import React, { useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { Alert, AvatarLabelGroup, Badge, Button, CollapsibleSection, Radio, RadioGroup, Table } from "@opendate-app/setlist";
import { toastError } from "../../../shared/toastHelper";
import { attendanceBadge, NOT_YET } from "../constants/attendanceStatus";
import initialsFor from "../../ClassesShared/initials";
import personColorProps from "../../ClassesShared/personColor";

const attendedSummary = (roster) => `${roster.filter((row) => row.attendance_status === "attended").length} of ${roster.length} attended`;

export default function SessionAttendance({ attendancePath, csrfToken }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  const load = async () => {
    try {
      const resp = await axios.get(attendancePath, { params: { format: "json" } });
      setData(resp.data);
      setLoadFailed(false);
    } catch (e) {
      setLoadFailed(true);
    }
  };

  const onOpenChange = (next) => {
    setOpen(next);
    if (next) load();
  };

  const save = async (request, describe) => {
    if (saving) return;
    setSaving(true);
    try {
      const resp = await request();
      setData(resp.data);
      setAnnouncement(`${describe}. ${attendedSummary(resp.data.roster)}`);
    } catch (e) {
      toastError(e.response?.data?.errors?.base?.[0] || "Could not save attendance. The roster has been refreshed.");
      await load();
    } finally {
      setSaving(false);
    }
  };

  const headers = { "X-CSRF-Token": csrfToken };
  const mark = (row, status) => {
    const describe = `Marked ${row.full_name} as ${attendanceBadge(status === NOT_YET ? null : status).label.toLowerCase()}`;
    if (status === NOT_YET) {
      return save(() => axios.delete(`${attendancePath}/${row.attendance_id}`, { headers }), describe);
    }
    return save(() => axios.post(attendancePath, { class_registration_id: row.id, status }, { headers }), describe);
  };
  const markAll = () => save(() => axios.post(`${attendancePath}/mark_all_attended`, {}, { headers }), "Marked everyone as attended");

  const roster = data?.roster || [];
  const notYetCount = roster.filter((row) => !row.attendance_status).length;
  const canMark = Boolean(data?.canMark);

  const columns = [
    {
      key: "name",
      header: "Attendee",
      primary: true,
      render: (row) => (
        <AvatarLabelGroup size="sm" initials={initialsFor(row.full_name)} {...personColorProps(row.full_name)} name={row.full_name} supportingText={row.email} />
      ),
    },
    {
      key: "status",
      header: "Attendance",
      render: (row) => {
        const badge = attendanceBadge(row.attendance_status);
        return <Badge color={badge.color} size="sm" dot>{badge.label}</Badge>;
      },
    },
  ];
  if (canMark) {
    columns.push({
      key: "mark",
      header: "Mark",
      render: (row) => (
        <div role="radiogroup" aria-label={`Attendance for ${row.full_name}`} aria-busy={saving}>
          <RadioGroup
            name={`attendance-${row.id}`}
            size="sm"
            value={row.attendance_status || NOT_YET}
            onChange={(status) => mark(row, status)}
          >
            <div className="d-flex flex-wrap" style={{ gap: 12 }}>
              <Radio value="attended" label="Attended" />
              <Radio value="no_show" label="No-show" />
              <Radio value={NOT_YET} label="Not yet" />
            </div>
          </RadioGroup>
        </div>
      ),
    });
  }

  return (
    <div className="mt-3" data-testid="session-attendance">
      <CollapsibleSection
        size="sm"
        title="Attendance"
        meta={data ? attendedSummary(roster) : null}
        open={open}
        onOpenChange={onOpenChange}
      >
        <span className="sr-only" role="status">{announcement}</span>
        {loadFailed && <Alert color="error" role="alert" description="Could not load the roster." />}
        {canMark && roster.length > 0 && (
          <div className="d-flex justify-content-end mb-2">
            <Button variant="secondary" size="sm" onClick={markAll} disabled={notYetCount === 0} aria-busy={saving}>
              Mark All Attended
            </Button>
          </div>
        )}
        {!loadFailed && (
          <Table className="classes-table"
            columns={columns}
            data={roster}
            loading={!data}
            getRowKey={(row) => row.id}
            stackBelow={560}
            empty="Nobody is registered for this class yet."
          />
        )}
      </CollapsibleSection>
    </div>
  );
}

SessionAttendance.propTypes = {
  attendancePath: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
};
